import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, Pressable, Modal, ScrollView, Image, Alert, Platform, Linking } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useDropzone } from 'react-dropzone';

interface PhotosModalProps {
    visible: boolean;
    onClose: () => void;
    title: string;
    photos: Array<{
        id: string;
        uri: string;
        caption?: string;
        timestamp: string;
        author: string;
    }>;
    onAddPhoto: (uri: string, caption?: string) => void;
    canAdd?: boolean;
    onDeletePhoto?: (photoId: string, uri: string) => void;
}

const PhotosModal: React.FC<PhotosModalProps> = ({
    visible,
    onClose,
    title,
    photos,
    onAddPhoto,
    canAdd = true,
    onDeletePhoto
}) => {
    const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        setIsUploading(true);
        try {
            const file = acceptedFiles[0];

            // Create a local URL for the file
            const reader = new FileReader();
            reader.onload = async (e) => {
                if (e.target?.result) {
                    const dataUrl = e.target.result as string;
                    await onAddPhoto(dataUrl, '');
                }
                setIsUploading(false);
            };
            reader.readAsDataURL(file);
        } catch (error) {
            console.error('Error processing dropped file:', error);
            Alert.alert('Error', 'Failed to process image. Please try again.');
            setIsUploading(false);
        }
    }, [onAddPhoto]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
        },
        multiple: false,
        disabled: isUploading
    });


    const handleDownload = async (uri: string) => {
        try {
            if (!uri) return;
            if (Platform.OS === 'web') {
                const anchor = document.createElement('a');
                anchor.href = uri;
                anchor.download = uri.split('/').pop() || 'photo.jpg';
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            } else {
                await Linking.openURL(uri);
            }
        } catch (e) {
            Alert.alert('Download failed', 'Unable to download this photo.');
        }
    };

    const formatTimestamp = (timestamp: string) => {
        return new Date(timestamp).toLocaleDateString();
    };

    return (
        <Modal visible={visible} animationType="slide" transparent={true}>
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <View style={styles.headerLeft}>
                            <MaterialCommunityIcons name="camera" size={24} color="#2C67D6" />
                            <Text style={styles.modalTitle}>{title}</Text>
                        </View>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={24} color="#666" />
                        </Pressable>
                    </View>

                    {/* Photos Grid */}
                    <ScrollView style={styles.photosContainer} showsVerticalScrollIndicator={false}>
                        {photos.length > 0 ? (
                            <View style={styles.photosGrid}>
                                {photos.map((photo) => (
                                    <Pressable
                                        key={photo.id}
                                        style={styles.photoItem}
                                        onPress={() => setSelectedPhoto(photo.uri)}
                                    >
                                        <View style={{ position: 'relative' }}>
                                            <Image
                                                source={{ uri: photo.uri }}
                                                style={styles.photoImage}
                                                resizeMode="cover"
                                            />
                                            <View style={styles.photoActionButtonsOverlay}>
                                                <Pressable
                                                    style={styles.photoActionButton}
                                                    onPress={(e) => { e.stopPropagation(); handleDownload(photo.uri); }}
                                                >
                                                    <MaterialCommunityIcons name="download" size={20} color="#fff" />
                                                </Pressable>
                                                {onDeletePhoto && (
                                                    <Pressable
                                                        style={styles.photoActionButton}
                                                        onPress={(e) => {
                                                            e.stopPropagation();
                                                            onDeletePhoto(photo.id, photo.uri);
                                                        }}
                                                    >
                                                        <MaterialCommunityIcons name="trash-can" size={20} color="#fff" />
                                                    </Pressable>
                                                )}
                                            </View>
                                        </View>
                                        {photo.caption && photo.caption.trim() && (
                                            <Text style={styles.photoCaption} numberOfLines={2}>
                                                {photo.caption}
                                            </Text>
                                        )}
                                    </Pressable>
                                ))}
                            </View>
                        ) : (
                            <View style={styles.emptyState}>
                                <MaterialCommunityIcons name="camera-outline" size={48} color="#ccc" />
                                <Text style={styles.emptyStateText}>No photos yet</Text>
                            </View>
                        )}
                    </ScrollView>

                    {/* Dropzone Area - Only show for users with permission */}
                    {canAdd && (
                        <View style={styles.addPhotoContainer}>
                            <div {...getRootProps()} style={{
                                border: '2px dashed #ccc',
                                borderRadius: '12px',
                                padding: '40px',
                                textAlign: 'center' as const,
                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                backgroundColor: isDragActive ? '#e3f2fd' : '#f8f9fa',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                flexDirection: 'column' as const,
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '120px',
                                borderColor: isDragActive ? '#2C67D6' : '#ccc',
                                opacity: isUploading ? 0.6 : 1
                            }}>
                                <input {...getInputProps()} />
                                <MaterialCommunityIcons
                                    name={isUploading ? "loading" : "cloud-upload"}
                                    size={32}
                                    color={isDragActive ? "#2C67D6" : "#666"}
                                />
                                <Text style={{
                                    fontSize: 14,
                                    color: isDragActive ? '#2C67D6' : '#666',
                                    marginTop: 8,
                                    textAlign: 'center' as const,
                                    fontWeight: isDragActive ? '600' : '500'
                                }}>
                                    {isUploading ? 'Uploading...' :
                                        isDragActive ? 'Drop photo here' :
                                            'Drag & drop photo here or click to select'}
                                </Text>
                            </div>
                        </View>
                    )}
                </View>
            </View>

            {/* Full Screen Photo Modal */}
            {selectedPhoto && (
                <Modal visible={true} animationType="fade" transparent={true}>
                    <View style={styles.fullScreenOverlay}>
                        <Pressable
                            style={styles.fullScreenClose}
                            onPress={() => setSelectedPhoto(null)}
                        >
                            <MaterialCommunityIcons name="close" size={30} color="white" />
                        </Pressable>
                        <Pressable
                            style={styles.fullScreenDownload}
                            onPress={() => handleDownload(selectedPhoto)}
                        >
                            <MaterialCommunityIcons name="download" size={24} color="white" />
                        </Pressable>
                        <Image
                            source={{ uri: selectedPhoto }}
                            style={styles.fullScreenImage}
                            resizeMode="contain"
                        />
                    </View>
                </Modal>
            )}
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContainer: {
        width: '90%',
        maxWidth: 500,
        height: '70%',
        backgroundColor: 'white',
        borderRadius: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 16,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
        marginLeft: 8,
    },
    closeButton: {
        padding: 4,
    },
    photosContainer: {
        flex: 1,
        padding: 20,
    },
    photosGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    photoItem: {
        width: '48%',
        marginBottom: 16,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    photoImage: {
        width: '100%',
        height: 120,
    },
    photoActionButtonsOverlay: {
        position: 'absolute',
        top: 8,
        right: 8,
        flexDirection: 'row',
        gap: 8,
    },
    photoActionButton: {
        backgroundColor: 'rgba(0,0,0,0.5)',
        borderRadius: 20,
        padding: 8,
    },
    photoCaption: {
        padding: 8,
        fontSize: 12,
        color: '#666',
        lineHeight: 16,
    },
    emptyState: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
    },
    emptyStateText: {
        fontSize: 16,
        color: '#999',
        marginTop: 16,
        textAlign: 'center',
    },
    addPhotoContainer: {
        padding: 20,
        borderTopWidth: 1,
        borderTopColor: '#e9ecef',
    },

    fullScreenOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    fullScreenClose: {
        position: 'absolute',
        top: 50,
        right: 20,
        zIndex: 1,
        padding: 10,
    },
    fullScreenDownload: {
        position: 'absolute',
        top: 50,
        right: 70,
        zIndex: 1,
        padding: 10,
    },
    fullScreenImage: {
        width: '90%',
        height: '70%',
    },
});

export default PhotosModal; 